import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatINR, formatINRFull } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { QueryForm } from "@/components/site/QueryForm";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Star, Check, X, FileText, ShieldCheck, ArrowUpRight } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

export default function BankPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [amount, setAmount] = useState(500000);
  const [tenure, setTenure] = useState(36);

  useEffect(() => {
    setLoading(true);
    api.get(`/banks/${slug}`).then((r) => setBank(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  const rate = bank?.interest_rate_min || 11;
  const { emi, totalInterest, totalPayable } = useMemo(() => {
    const r = rate / 12 / 100;
    const emiVal = (amount * r * Math.pow(1 + r, tenure)) / (Math.pow(1 + r, tenure) - 1);
    return {
      emi: isFinite(emiVal) ? emiVal : 0,
      totalInterest: emiVal * tenure - amount,
      totalPayable: emiVal * tenure,
    };
  }, [amount, tenure, rate]);

  const scrollToForm = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  const pageTitle = bank ? `${bank.name} Personal Loan — Interest Rate ${bank.interest_rate_min}% & EMI Calculator | Loan81` : "Personal Loan | Loan81";
  useEffect(() => { document.title = pageTitle; }, [pageTitle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header onCTA={scrollToForm} />
        <div className="max-w-7xl mx-auto px-6 py-16 space-y-4">
          <Skeleton className="h-8 w-48" /><Skeleton className="h-16 w-96" /><Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (notFound || !bank) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header onCTA={scrollToForm} />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="font-display text-3xl font-semibold text-slate-900">Bank not found</div>
          <p className="text-slate-600 mt-2">We couldn't find that lender. Browse all our partners on the comparison page.</p>
          <Button onClick={() => navigate("/")} className="mt-6">Go home</Button>
        </div>
      </div>
    );
  }

  const chartData = [
    { name: "Principal", value: amount },
    { name: "Interest", value: Math.max(totalInterest, 0) },
  ];
  const COLORS = ["#0F172A", "#10B981"];

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`bank-page-${bank.id}`}>
      <Header onCTA={scrollToForm} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="hero-blob w-[400px] h-[400px] bg-emerald-100/60 -top-40 -right-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> All Lenders
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 font-display font-bold text-white text-2xl" style={{ backgroundColor: bank.logo_color }}>
                  {bank.short_name.charAt(0)}
                </div>
                <div>
                  <Badge variant="secondary" className="text-[10px] font-semibold h-5 px-1.5 bg-slate-100 mb-1">{bank.type}</Badge>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-0.5 text-sm text-slate-600">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {bank.rating.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-slate-900 leading-tight">
                {bank.name}<br /><span className="text-emerald-600">Personal Loan</span>
              </h1>
              <p className="mt-4 text-lg text-slate-600">{bank.tagline}</p>
              <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">{bank.description}</p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button data-testid="bank-apply-hero" onClick={scrollToForm} className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-6 font-semibold rounded-md active:scale-95 transition-all">
                  Get Called Back <ArrowUpRight className="ml-1 w-4 h-4" />
                </Button>
                <Button variant="outline" asChild className="h-12 px-6 font-semibold border-slate-300">
                  <Link to="/#compare">Compare with other banks</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-slate-900 text-white rounded-2xl p-6 lg:p-8">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-4">Loan Snapshot</div>
                <div className="grid grid-cols-2 gap-5">
                  <SnapItem label="Interest Rate" value={`${bank.interest_rate_min}%`} sub={`up to ${bank.interest_rate_max}%`} />
                  <SnapItem label="Processing Fee" value={`${bank.processing_fee_min}%`} sub={`up to ${bank.processing_fee_max}%`} />
                  <SnapItem label="Max Loan" value={formatINR(bank.max_loan_amount)} sub={`up to ${bank.max_tenure_months} months`} />
                  <SnapItem label="Min Credit Score" value={String(bank.min_credit_score)} sub={`Income ₹${(bank.min_income/1000).toFixed(0)}K+/mo`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EMI Calculator */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">EMI Calculator</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-slate-900">
              {bank.short_name} Personal Loan EMI Calculator
            </h2>
            <p className="mt-2 text-slate-600">Calculated at {bank.short_name}'s starting rate of {bank.interest_rate_min}% p.a.</p>
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 space-y-8">
              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Loan Amount</label>
                  <span className="font-display font-semibold text-slate-900" data-testid="bank-emi-amount-value">{formatINRFull(amount)}</span>
                </div>
                <Slider data-testid="bank-emi-amount" min={50000} max={bank.max_loan_amount} step={10000} value={[amount]} onValueChange={(v) => setAmount(v[0])} />
              </div>

              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Tenure</label>
                  <span className="font-display font-semibold text-slate-900" data-testid="bank-emi-tenure-value">{tenure} months</span>
                </div>
                <Slider data-testid="bank-emi-tenure" min={6} max={bank.max_tenure_months} step={6} value={[tenure]} onValueChange={(v) => setTenure(v[0])} />
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-200">
                <Metric label="Monthly EMI" value={formatINRFull(emi)} accent testId="bank-emi-monthly" />
                <Metric label="Total Interest" value={formatINR(totalInterest)} />
                <Metric label="Total Payable" value={formatINR(totalPayable)} />
              </div>

              <Button onClick={scrollToForm} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md font-semibold active:scale-95 transition-all">
                Apply at {bank.short_name} through Loan81
              </Button>
            </div>

            <div className="bg-slate-900 text-white rounded-2xl p-8">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2">Breakdown</div>
              <div className="font-display text-xl font-medium mb-4">Principal vs Interest</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
                      {chartData.map((_, i) => <Cell key={i} fill={COLORS[i]} stroke="none" />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatINRFull(v)} contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8 }} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#fff" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="flex items-center gap-2"><span className="w-3 h-3 bg-slate-100 rounded-sm" />Principal</span><span className="font-semibold">{formatINRFull(amount)}</span></div>
                <div className="flex justify-between"><span className="flex items-center gap-2"><span className="w-3 h-3 bg-emerald-500 rounded-sm" />Interest</span><span className="font-semibold">{formatINRFull(totalInterest)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility + Docs + Pros/Cons */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <Card icon={<ShieldCheck className="w-5 h-5" />} title="Eligibility">
            <ul className="space-y-2 mt-4">
              {bank.eligibility.map((e, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{e}</span></li>
              ))}
            </ul>
          </Card>

          <Card icon={<FileText className="w-5 h-5" />} title="Documents Required">
            <ul className="space-y-2 mt-4">
              {bank.documents.map((d, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{d}</span></li>
              ))}
            </ul>
          </Card>

          {bank.pros?.length > 0 && (
            <Card title="Pros" accent>
              <ul className="space-y-2 mt-4">
                {bank.pros.map((p, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{p}</span></li>
                ))}
              </ul>
            </Card>
          )}

          {bank.cons?.length > 0 && (
            <Card title="Watch out for">
              <ul className="space-y-2 mt-4">
                {bank.cons.map((c, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-700"><X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" /><span>{c}</span></li>
                ))}
              </ul>
            </Card>
          )}

          <Card title="Key Features" className="md:col-span-2">
            <div className="mt-4 flex flex-wrap gap-2">
              {bank.features.map((f) => (
                <span key={f} className="text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">{f}</span>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <QueryForm presetBank={bank.name} />
      <Footer />
    </div>
  );
}

const SnapItem = ({ label, value, sub }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-400 mb-1">{label}</div>
    <div className="font-display text-2xl font-semibold">{value}</div>
    <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
  </div>
);

const Metric = ({ label, value, accent, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{label}</div>
    <div className={`font-display font-semibold text-xl ${accent ? "text-emerald-600" : "text-slate-900"}`} data-testid={testId}>{value}</div>
  </div>
);

const Card = ({ icon, title, children, accent, className = "" }) => (
  <div className={`bg-white border ${accent ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"} rounded-2xl p-6 ${className}`}>
    <div className="flex items-center gap-2">
      {icon && <span className="text-emerald-600">{icon}</span>}
      <div className="font-display font-semibold text-lg text-slate-900">{title}</div>
    </div>
    {children}
  </div>
);
